import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorMessage } from "@/lib/utils";
import { motion } from "framer-motion";
import { TreePine, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import MapLocationPicker from "@/components/MapLocationPicker";
import SavedCardsSelector from "@/components/SavedCardsSelector";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";



const LawnMowingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState("new");
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (user) setFormData(prev => ({ ...prev, customer_name: user.name || "", customer_phone: user.phone || "", customer_email: user.email || "" }));
  }, [user]);

  const [formData, setFormData] = useState({
    service_address: "",
    service_type: "",
    yard_size: "",
    frequency: "one_time",
    notes: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
  });

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const serviceTypes = [
    { value: "lawn_mowing", label: "Lawn Mowing", desc: "Yard size price only" },
    { value: "mowing_trimming", label: "Mowing & Trimming", desc: "+$20" },
    { value: "full_yard_care", label: "Full Yard Care", desc: "+$30" },
    { value: "leaf_cleanup", label: "Leaf / Debris Cleanup", desc: "+$15" },
  ];

  const getEstimate = () => {
    if (!formData.service_address || !formData.service_type || !formData.yard_size) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!pickupCoords) {
      toast.error("Please select a valid location on the map");
      return;
    }
    const sizeM = { small: 0.7, medium: 1.0, large: 1.5 }[formData.yard_size] || 1.0;
    const freqM = { one_time: 1.0, weekly: 0.85, biweekly: 0.9, monthly: 0.95 }[formData.frequency] || 1.0;
    const price = Math.round(45.0 * sizeM * freqM * 100) / 100;
    setStep(2);
    return price;
  };

  const estimatedPrice = (() => {
    const yardPrices = { small: 50, medium: 90, large: 130 };
    const serviceAddon = { lawn_mowing: 0, mowing_trimming: 20, full_yard_care: 30, leaf_cleanup: 15 };
    const base = yardPrices[formData.yard_size] || 90;
    const addon = serviceAddon[formData.service_type] || 0;
    return base + addon;
  })();

  const proceedToPayment = async () => {
    setPaymentLoading(true);
    try {
      const response = await axios.post(`${API}/lawn-mowing/booking`, { ...formData, pickup_coords: pickupCoords });
      const booking = response.data;
      const amount = booking.estimated_price || estimatedPrice;

      const serviceData = {
        booking_id: booking.id,
        booking_type: "lawn_mowing",
        amount,
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        service_type: "lawn_mowing",
        pickup_address: formData.service_address,
        pickup_coords: pickupCoords,
      };

      if (selectedCardId && selectedCardId !== "new") {
        const authResponse = await axios.post(`${API}/payments/authorize-hold`, {
          ...serviceData,
          payment_method_id: selectedCardId,
        }, { withCredentials: true });
        if (authResponse.data.success) {
          toast.success("Payment authorized! Finding your professional...");
          navigate("/matching", {
            state: { booking_id: booking.id, booking_type: "lawn_mowing", service_type: "lawn_mowing", pickup_lat: pickupCoords.latitude, pickup_lng: pickupCoords.longitude, pickup_address: formData.service_address },
          });
        } else {
          toast.error("Payment authorization failed.");
        }
      } else {
        const paymentResponse = await axios.post(`${API}/payments/create-checkout`, {
          ...serviceData,
          origin_url: window.location.origin,
        });
        window.location.href = paymentResponse.data.checkout_url;
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Payment failed. Please try again."));
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="lawn-mowing-page">
      <Navbar />

      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-green-950 to-green-900" />
        <div className="relative section-container text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 bg-green-800/50 border border-green-600/30 text-green-200 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <TreePine className="w-4 h-4" /> Lawn Care Services
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Lawn Mowing <span className="text-green-300">On Demand</span>
            </h1>
            <p className="text-base text-green-200 max-w-xl mx-auto">
              Professional lawn care at your doorstep. Mowing, trimming, edging, and full yard cleanup.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-container py-12">
        <div className="max-w-3xl mx-auto">
          {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="glass-card p-8" data-testid="lawn-step-1">
            <h2 className="heading-3 text-emerald-950 mb-8">Service Details</h2>
            <div className="space-y-6">
              <MapLocationPicker
                pickupAddress={formData.service_address}
                onPickupChange={(val) => handleInputChange("service_address", val)}
                onPickupCoordsChange={setPickupCoords}
                pickupLabel="Property Address *"
                pickupPlaceholder="Enter your address"
                singleLocation={true}
                serviceType="lawn_mowing"
              />

              <div>
                <Label className="text-emerald-950 font-medium mb-3 block">Service Type *</Label>
                <div className="grid grid-cols-2 gap-3">
                  {serviceTypes.map((svc) => (
                    <button key={svc.value} type="button" onClick={() => handleInputChange("service_type", svc.value)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${formData.service_type === svc.value ? "border-green-500 bg-green-50" : "border-stone-200 hover:border-green-300"}`}
                      data-testid={`service-${svc.value}`}
                    >
                      <p className="font-medium text-emerald-950 text-sm">{svc.label}</p>
                      <p className="text-xs text-stone-500 mt-0.5">{svc.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-emerald-950 font-medium mb-2 block">Yard Size *</Label>
                <Select value={formData.yard_size} onValueChange={(v) => handleInputChange("yard_size", v)}>
                  <SelectTrigger className="input-style" data-testid="yard-size-select">
                    <SelectValue placeholder="Select yard size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small (under 2,000 sq ft) — $50</SelectItem>
                    <SelectItem value="medium">Medium (2,000 - 5,000 sq ft) — $90</SelectItem>
                    <SelectItem value="large">Large (over 5,000 sq ft) — $130</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-emerald-950 font-medium mb-2 block">Additional Notes</Label>
                <Textarea className="input-style min-h-[80px]" placeholder="Gate codes, pet info, specific areas to focus on..." value={formData.notes} onChange={(e) => handleInputChange("notes", e.target.value)} data-testid="notes-input" />
              </div>

              <Button className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full py-6 font-bold text-lg" onClick={getEstimate} disabled={loading} data-testid="continue-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <>Continue <ArrowRight className="w-5 h-5 ml-2" /></>}
              </Button>
            </div>
          </motion.div>
          )}

          {/* Step 2: Payment */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8 mt-6" data-testid="payment-step">
              <h2 className="text-xl font-bold text-emerald-950 mb-4">Payment</h2>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-stone-600">Estimated Price</p>
                <p className="text-3xl font-bold text-green-700">${estimatedPrice.toFixed(2)} <span className="text-sm font-normal text-stone-500">CAD</span></p>
              </div>
              <SavedCardsSelector onSelect={setSelectedCardId} selectedCardId={selectedCardId} />
              <Button className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full py-6 font-bold mt-4" onClick={proceedToPayment} disabled={paymentLoading} data-testid="pay-btn">
                {paymentLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <>Pay & Find Professional <ArrowRight className="w-5 h-5 ml-2" /></>}
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LawnMowingPage;
